import { createColumnHelper } from "@tanstack/react-table";
import GenericTable, { GenericTableProps } from "components/Table/GenericTable";
import { ResponseModel } from "services/ApiClient/responseModel";
import apiClient from "services/ApiClient";
import { RandomEmptyIssueState } from "components/EmptyComponents/RandomEmptyState";
import { Flex, HStack, Link } from "@chakra-ui/react";
import CDS from "design-system";
import { Link as RouterLink, generatePath } from "react-router";
import SearchBar from "components/SearchBar";
import { useMemo, useState } from "react";
import { ResolveNewHireIssuesButton } from "features/Issues/Components/Button/ResolveButton";
import { newHireJourneyKeys } from "features/NewHireJourney/hooks";
import { TableColumnListIssuesByNewHire } from "features/Issues/Components/ListIssues/ListIssuesByNewHire";
import { apiNewHireWithActiveIssuesListPath } from "helpers/url/api/v2";
import { NewHireJourney } from "models/v2/newHireJourney";
import { INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { basicShortDate } from "helpers/time";
import { Text12 } from "components/Text";

const columnHelper = createColumnHelper<NewHireJourney>();

const columnDefs = [
  columnHelper.accessor((row) => row, {
    id: "newHireJourneyId",
    header: "New Hire",
    cell: (info) => {
      const journey = info.getValue();
      return (
        <Link
          as={RouterLink}
          _hover={{ textDecoration: "none" }}
          to={generatePath(INTERNAL_TEAM_ROUTES.newHires.show, { id: journey.id })}
          width="fit-content"
        >
          <HStack spacing="3">
            <Flex direction="column">
              <CDS.Text14 fontWeight="medium">{journey.newHire?.fullName || "Unknown"}</CDS.Text14>
              <CDS.Text12 color="fg.muted">
                {journey.newHire?.title || ""}
              </CDS.Text12>
            </Flex>
          </HStack>
        </Link>
      );
    },
    maxSize: 24,
    minSize: 24,
    size: 24,
  }),
  columnHelper.accessor((row) => row, {
    id: "startDate",
    cell: (row) => {
      const date = row.getValue()?.startDate;
      return date ? <Text12>{basicShortDate(date)}</Text12> : <Text12 color="gray.400">—</Text12>;
    },
    header: "Start Date",
    meta: {
      sx: {
        width: "120px",
        minWidth: "120px",
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "issues",
    header: "Issues",
    cell: (row) => <TableColumnListIssuesByNewHire newHireId={row.getValue()?.id} />,
    enableSorting: false,
    maxSize: 64,
    minSize: 64,
    size: 64,
  }),
  columnHelper.accessor((row) => row, {
    id: "resolution",
    header: "",
    cell: (row) => <ResolveNewHireIssuesButton newHireId={row.getValue()?.id} />,
    enableSorting: false,
    maxSize: 20,
    minSize: 20,
    size: 20,
  }),
];

interface IssuesByNewHireTableProps {
  tableDefaultSort?: { sortBy: string; direction: "asc" | "desc" };
}

export default function IssuesByNewHireTable({
  tableDefaultSort = { sortBy: "startDate", direction: "desc" },
}: IssuesByNewHireTableProps) {
  const [searchText, setSearchText] = useState("");

  const additionalQueries = useMemo(() => {
    const queries: Record<string, string> = {};
    if (searchText && searchText.length > 2) {
      queries.q = searchText;
    }
    return queries;
  }, [searchText]);

  const tableProps: GenericTableProps<NewHireJourney> = {
    fetchData: (queryString, signal) =>
      apiClient
        .performRequest<ResponseModel<NewHireJourney[]>>(apiNewHireWithActiveIssuesListPath(queryString), { signal })
        .then((response) => response.payload),
    queryKey: newHireJourneyKeys.withActiveIssuesList(),
    additionalQueries,
    columns: columnDefs,
    meta: {
      pagination: { perPage: 20, initialPage: 1 },
      sort: [tableDefaultSort],
    },
    textWrap: true,
    emptyStateComponent: <RandomEmptyIssueState />,
    variant: "simple",
  };

  return (
    <>
      <Flex justify="flex-start" align="center">
        <SearchBar minWidth="500" name="actionsSearch" onInput={setSearchText} my="4" />
      </Flex>
      <GenericTable {...tableProps} />
    </>
  );
}
